/**
 * Workspaces Service
 * Handles all workspace-related data operations with workspace-first RLS
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { handleError, getSupabaseErrorMessage } from '@/lib/errorHandler';

export type Workspace = Tables<'workspaces'>;
export type WorkspaceMember = Tables<'workspace_members'>;
export type UserRole = Tables<'user_roles'>;

export class WorkspacesService {
  /**
   * Get current user's workspace ID from workspace_members
   * Returns the first workspace where user is a member
   */
  static async getCurrentWorkspaceId(userId: string): Promise<string | null> {
    logger.supabase('SELECT', 'workspace_members', { userId });
    
    const { data, error } = await supabase
      .from('workspace_members')
      .select('workspace_id')
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error('[WorkspacesService] Error fetching workspace_id', error, { userId });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data?.workspace_id || null;
  }

  /**
   * Get workspace by ID (with RLS check)
   */
  static async getWorkspace(workspaceId: string): Promise<Workspace | null> {
    const { data, error } = await supabase
      .from('workspaces')
      .select('*')
      .eq('id', workspaceId)
      .maybeSingle();

    if (error) {
      console.error('[WorkspacesService] Error fetching workspace:', error);
      throw new Error(`Failed to get workspace: ${error.message}`);
    }

    return data as Workspace | null;
  }

  /**
   * Get user's role in workspace
   */
  static async getUserRole(workspaceId: string, userId: string): Promise<UserRole['role'] | null> {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('[WorkspacesService] Error fetching user role:', error);
      return null;
    }

    return data?.role || null;
  }

  /**
   * Get all members of a workspace
   */
  static async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    const { data, error } = await supabase
      .from('workspace_members')
      .select('*')
      .eq('workspace_id', workspaceId);

    if (error) {
      console.error('[WorkspacesService] Error fetching members:', error);
      throw new Error(`Failed to get members: ${error.message}`);
    }

    return (data || []) as WorkspaceMember[];
  }

  /**
   * Check if user is member of workspace
   */
  static async isMember(workspaceId: string, userId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('workspace_members')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('[WorkspacesService] Error checking membership:', error);
      return false;
    }

    return !!data;
  }
}

